import React, { type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { useIntl, type MessageDescriptorV2 } from '@atlassian/jira-intl';
import messages from './messages';

export type OnClickCallback = (_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => void;

export type BaseProps = {
	onClose: () => void;
	onContinue: OnClickCallback;
	titleText: MessageDescriptorV2;
	bodyText?: MessageDescriptorV2;
	cancelText: MessageDescriptorV2;
	continueText: MessageDescriptorV2;
};

export type Props = Pick<BaseProps, 'onClose' | 'onContinue'>;

/**
 * `DiscardModalBase` base modal component factory that matches
 * the given design.
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
export const DiscardModalBase = ({
	onClose,
	onContinue,
	titleText,
	bodyText,
	cancelText,
	continueText,
}: BaseProps) => {
	const { formatMessage } = useIntl();
	return (
		<Modal
			testId="issue-create.ui.modal.confirm-dismiss-ai-suggested-modal.modal"
			onClose={onClose}
		>
			<ModalHeader>
				<ModalTitle appearance="warning" isMultiline>
					{formatMessage(titleText)}
				</ModalTitle>
			</ModalHeader>
			{bodyText && (
				<ModalBody testId="issue-create.ui.modal.confirm-dismiss-ai-suggested-modal.body">
					{formatMessage(bodyText)}
				</ModalBody>
			)}
			<ModalFooter>
				<Button
					appearance="subtle"
					aria-label={formatMessage(cancelText)}
					testId="issue-create.ui.modal.confirm-dismiss-ai-suggested-modal.cancel-button"
					onClick={onClose}
				>
					{formatMessage(cancelText)}
				</Button>
				<Button
					appearance="warning"
					testId="issue-create.ui.modal.confirm-dismiss-ai-suggested-modal.discard-button"
					aria-label={formatMessage(continueText)}
					onClick={onContinue}
				>
					{formatMessage(continueText)}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

/**
 * `DiscardSuggestionsAllFromGlobalCreateModal` modal triggered when AI minimized modal
 * is open while user clicks the global [Create] button.
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
export const DiscardSuggestionsAllFromGlobalCreateModal = ({ onClose, onContinue }: Props) => (
	<DiscardModalBase
		onClose={onClose}
		onContinue={onContinue}
		titleText={messages.modalTitleDiscardAllFromGlobalCreate}
		bodyText={messages.modalBodyDiscardAllFromGlobalCreate}
		cancelText={messages.modalCancelButton}
		continueText={messages.modalDiscardButton}
	/>
);

/**
 * `DiscardSuggestionsCurrentSuggested` modal triggered when AI minimized modal
 * is open while user clicks "Discrad" issue on the minimizable modal.
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
export const DiscardSuggestionsCurrentSuggested = ({ onClose, onContinue }: Props) => (
	<DiscardModalBase
		onClose={onClose}
		onContinue={onContinue}
		titleText={messages.modalTitleDiscardCurrentSuggested}
		bodyText={messages.modalBodyDiscardCurrentSuggested}
		cancelText={messages.modalCancelButton}
		continueText={messages.modalDiscardButton}
	/>
);

/**
 * `DiscardSuggestionsCurrentEdited` modal triggered when AI minimized modal
 * is open and edited, while user navigates to a different issue.
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
export const DiscardSuggestionsCurrentEdited = ({ onClose, onContinue }: Props) => (
	<DiscardModalBase
		onClose={onClose}
		onContinue={onContinue}
		titleText={messages.modalTitleDiscardCurrentEdited}
		bodyText={messages.modalBodyDiscardCurrentEdited}
		cancelText={messages.modalCancelButton}
		continueText={messages.modalContinueButton}
	/>
);
